import React from 'react';

type ListBlock = {
  ordered: boolean;
  items: ListItem[];
};

type ListItem = {
  lines: string[];
  nested: ListBlock | null;
};

type Block =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; level: number; text: string }
  | { type: 'code'; text: string }
  | { type: 'list'; list: ListBlock };

const indentOf = (line: string) =>
  (line.match(/^(\s*)/)?.[1] ?? '').replace(/\t/g, '  ').length;

const isListLine = (line: string) => /^(\s*)([-*]|\d+\.)\s+\S/.test(line);

const listMarker = (line: string) => {
  const match = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(line);
  if (!match) return null;
  return { ordered: /^\d+\.$/.test(match[2]), text: match[3] };
};

const parseList = (
  lines: string[],
  start: number,
  baseIndent: number,
): { list: ListBlock; next: number } => {
  const first = listMarker(lines[start]);
  const ordered = first?.ordered ?? false;
  const items: ListItem[] = [];
  let index = start;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      const next = lines[index + 1];
      if (next && isListLine(next) && indentOf(next) >= baseIndent) {
        index += 1;
        continue;
      }
      break;
    }

    const indent = indentOf(line);
    if (isListLine(line) && indent === baseIndent) {
      const marker = listMarker(line);
      if (!marker || (items.length > 0 && marker.ordered !== ordered)) break;
      items.push({ lines: [marker.text], nested: null });
      index += 1;
      continue;
    }

    if (indent > baseIndent && items.length > 0) {
      const item = items[items.length - 1];
      if (isListLine(line)) {
        const nested = parseList(lines, index, indent);
        item.nested = nested.list;
        index = nested.next;
        continue;
      }
      item.lines.push(line.trim());
      index += 1;
      continue;
    }

    break;
  }

  return { list: { ordered, items }, next: index };
};

export const parseInsightsMarkdown = (source: string): Block[] => {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (line.trim().startsWith('```')) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line.trim());
    if (heading) {
      blocks.push({
        type: 'heading',
        level: heading[1].length,
        text: heading[2],
      });
      index += 1;
      continue;
    }

    if (isListLine(line)) {
      const parsed = parseList(lines, index, indentOf(line));
      blocks.push({ type: 'list', list: parsed.list });
      index = parsed.next;
      continue;
    }

    const paragraph = [line.trim()];
    index += 1;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isListLine(lines[index]) &&
      !lines[index].trim().startsWith('```') &&
      !/^#{1,3}\s+/.test(lines[index].trim())
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
  }

  return blocks;
};

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let buffer = '';
  let index = 0;
  let nodeIndex = 0;

  const flush = () => {
    if (!buffer) return;
    nodes.push(buffer);
    buffer = '';
  };

  while (index < text.length) {
    const rest = text.slice(index);
    const previous = index > 0 ? text[index - 1] : '';
    let consumed = 0;
    let node: React.ReactNode = null;

    const code = /^`([^`]+)`/.exec(rest);
    const bold =
      /^\*\*([\s\S]+?)\*\*/.exec(rest) || /^__([\s\S]+?)__/.exec(rest);
    const starItalic = /^\*([^*\n]+)\*/.exec(rest);
    const underscoreItalic = !/[\w]/.test(previous)
      ? /^_([^_\n]+)_/.exec(rest)
      : null;
    const link = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/.exec(rest);

    if (code && rest.startsWith('`')) {
      consumed = code[0].length;
      node = (
        <code
          key={`${keyPrefix}-c-${nodeIndex}`}
          className="rounded-v2-sm bg-v2-sunken px-1 py-0.5 font-mono text-[0.85em]"
        >
          {code[1]}
        </code>
      );
    } else if (bold && (rest.startsWith('**') || rest.startsWith('__'))) {
      consumed = bold[0].length;
      node = (
        <strong key={`${keyPrefix}-b-${nodeIndex}`} className="font-semibold">
          {renderInline(bold[1], `${keyPrefix}-b-${nodeIndex}`)}
        </strong>
      );
    } else if (starItalic && rest.startsWith('*')) {
      consumed = starItalic[0].length;
      node = (
        <em key={`${keyPrefix}-i-${nodeIndex}`}>
          {renderInline(starItalic[1], `${keyPrefix}-i-${nodeIndex}`)}
        </em>
      );
    } else if (
      underscoreItalic &&
      rest.startsWith('_') &&
      !/[\w]/.test(text[index + underscoreItalic[0].length] || '')
    ) {
      consumed = underscoreItalic[0].length;
      node = (
        <em key={`${keyPrefix}-i-${nodeIndex}`}>
          {renderInline(underscoreItalic[1], `${keyPrefix}-i-${nodeIndex}`)}
        </em>
      );
    } else if (link && rest.startsWith('[')) {
      consumed = link[0].length;
      node = (
        <a
          key={`${keyPrefix}-a-${nodeIndex}`}
          href={link[2]}
          target="_blank"
          rel="noreferrer"
          className="text-v2-accent underline underline-offset-2"
        >
          {link[1]}
        </a>
      );
    }

    if (node) {
      flush();
      nodes.push(node);
      nodeIndex += 1;
      index += consumed;
      continue;
    }

    buffer += text[index];
    index += 1;
  }

  flush();
  return nodes;
};

const MarkdownList: React.FC<{ list: ListBlock; nested?: boolean }> = ({
  list,
  nested = false,
}) => {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag
      className={`${list.ordered ? 'list-decimal' : 'list-disc'} space-y-1 pl-5 marker:text-v2-fg-3 ${
        nested ? 'mt-2' : 'my-2'
      }`}
    >
      {list.items.map((item, index) => (
        <li key={index}>
          <span>{renderInline(item.lines[0] || '', `li-${index}`)}</span>
          {item.lines.slice(1).map((line, lineIndex) => (
            <p key={lineIndex} className="mt-1">
              {renderInline(line, `li-${index}-${lineIndex}`)}
            </p>
          ))}
          {item.nested && <MarkdownList list={item.nested} nested />}
        </li>
      ))}
    </Tag>
  );
};

const InsightsMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const blocks = parseInsightsMarkdown(text);
  return (
    <div className="space-y-2 break-words text-v2-13 leading-5">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          return (
            <p key={index} className="font-semibold text-v2-fg">
              {renderInline(block.text, `h-${index}`)}
            </p>
          );
        }
        if (block.type === 'code') {
          return (
            <pre
              key={index}
              className="overflow-x-auto whitespace-pre-wrap rounded-v2 bg-v2-sunken p-2 font-mono text-v2-12 text-v2-fg"
            >
              {block.text}
            </pre>
          );
        }
        if (block.type === 'list') {
          return <MarkdownList key={index} list={block.list} />;
        }
        return <p key={index}>{renderInline(block.text, `p-${index}`)}</p>;
      })}
    </div>
  );
};

export default InsightsMarkdown;
